/**
 * A client's hub in one module — `/clients/:id/modules/o2c`.
 *
 * Clients → {client} → {Module} → this page → Data files or Audits. Two
 * compact cards, each shown only to a role that may use it (data entry sees
 * Data files, auditors Audits, administrators both). The two are told apart
 * at a glance: a step number, their own colour and icon, and a line on what
 * each is for. A role with one card
 * still lands here, so the Module crumb on a list always has somewhere to go.
 * Every client is in every module.
 */

import { Box } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../provider/auth';
import { PageHeader } from '../../provider/pageTitle';
import { useClientQuery } from '../../queries/intake';
import { useEveryModuleClientsQuery } from '../../queries/clients';
import { ErrorState, LoadingState } from '../shared/states';
import { MODULE_CODES, MODULE_LABELS } from './modules';
import { useModule } from './useModule';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { CARD_GRID_SX, ModuleCard } from './ClientModulesPage';
import {
	clientModuleCrumbs,
	flowAccess,
	hubCards,
	moduleTally,
	tallyText,
} from './flow';
import { createPageHeaderSubTitleForClients } from '../../shared/ui';

const CARD_TEXT = {
	documents: {
		blurb: 'Step 1 · Upload the client’s files, map their columns and group them',
		icon: <FolderOpenOutlinedIcon />,
	},
	audits: {
		blurb: 'Step 2 · Run the checks on those files, review exceptions, download reports',
		icon: <FactCheckOutlinedIcon />,
	},
} as const;

/* Each card's colour: the card reads `--accent*`, so setting them on its list
   item recolours the icon tile, the hover border and the top edge. Data files
   blue, Audits the brand teal. */
const CARD_TONE_SX = {
	documents: {
		'--accent': 'var(--info-solid)',
		'--accent-border': 'var(--info-border)',
		'--accent-surface': 'var(--info-surface)',
	},
	audits: {},
} as const;

export default function ModuleHubPage() {
	const { clientId = '' } = useParams();
	const { module } = useModule();
	const { permissions } = useAuth();
	const access = flowAccess(permissions);
	const client = useClientQuery(clientId);
	const everyModule = useEveryModuleClientsQuery();
	const label = MODULE_LABELS[module];

	if (client.isPending) return <LoadingState label="Loading the client…" />;
	if (client.error || !client.data) {
		return (
			<Box className="stack">
				<PageHeader
					breadcrumbs={[{ label: 'Clients', to: '/clients' }]}
					title={label}
				/>
				<ErrorState
					error={
						client.error ?? new Error('This client was not found.')
					}
					onRetry={client.refetch}
					hint={
						<>
							Every client is listed on{' '}
							<Link className="link" to="/clients">
								Clients
							</Link>
							.
						</>
					}
				/>
			</Box>
		);
	}

	const record = client.data;
	const tally = moduleTally(
		everyModule.lists[MODULE_CODES.indexOf(module)],
		clientId,
	);
	const crumbs = clientModuleCrumbs(
		{ id: clientId, name: record.name },
		module,
	);

	return (
		<Box className="stack">
			<PageHeader
				breadcrumbs={crumbs}
				title={label}
				subtitle={createPageHeaderSubTitleForClients(
					record.code,
					record.gstin,
				)}
			/>

			<Box
				component="ul"
				aria-label="Data files and audits"
				sx={CARD_GRID_SX}
			>
				{hubCards(access, clientId, module).map((card) => {
					return (
						<Box
							component="li"
							key={card.key}
							sx={{
								...CARD_TONE_SX[card.key],
								display: 'flex',
								minWidth: 0,
								'& > a': {
									borderTop: '3px solid var(--accent)',
								},
							}}
						>
							<ModuleCard
								icon={CARD_TEXT[card.key].icon}
								title={card.label}
								blurb={CARD_TEXT[card.key].blurb}
								to={card.to}
								detail={
									tally
										? tallyText(tally, {
												documents:
													card.key === 'documents',
												audits: card.key === 'audits',
											})
										: '…'
								}
							/>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
